import { useId, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { BULK_IMPORT_MAX_BYTES } from "@/lib/admin-api";

/**
 * apps/web :: components/admin :: ImportFilePicker
 *
 * Single-file picker for the bulk-import flow. Produces a `<label htmlFor>`
 * pair so screen-readers announce the input's purpose, and applies a
 * client-side size cap mirroring `BULK_IMPORT_MAX_BYTES` on `admin-api`.
 *
 * `onFileSelected` carries `null` whenever the picker is cleared OR when
 * the chosen file is too large — the host page surfaces an inline error
 * and disables the "Preview" button accordingly.
 */
export interface ImportFilePickerProps {
  onFileSelected: (file: File | null, error: string | null) => void;
  disabled?: boolean;
}

export default function ImportFilePicker({
  onFileSelected,
  disabled = false,
}: ImportFilePickerProps) {
  const { t } = useTranslation();
  const inputId = useId();

  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.currentTarget.files?.[0] ?? null;
    if (!file) {
      onFileSelected(null, null);
      return;
    }
    if (file.size > BULK_IMPORT_MAX_BYTES) {
      onFileSelected(
        null,
        t("admin.import.file.tooLarge", {
          maxKib: Math.floor(BULK_IMPORT_MAX_BYTES / 1024),
        }),
      );
      return;
    }
    onFileSelected(file, null);
  };

  return (
    <div className="flex flex-col gap-xs">
      <label
        htmlFor={inputId}
        className="text-xs font-semibold uppercase tracking-wide text-neutral-600"
      >
        {t("admin.import.file.label")}
      </label>
      <input
        id={inputId}
        data-testid="admin-import-file-input"
        type="file"
        accept=".csv,text/csv"
        disabled={disabled}
        onChange={handleChange}
        className="block w-full text-sm text-neutral-800 file:mr-md file:rounded-md file:border-0 file:bg-brand-primary file:px-md file:py-xs file:text-sm file:font-semibold file:text-white hover:file:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      />
      <p className="text-xs text-neutral-600">
        {t("admin.import.file.hint", {
          maxKib: Math.floor(BULK_IMPORT_MAX_BYTES / 1024),
        })}
      </p>
    </div>
  );
}
